import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import MatrizSeleccionDialog from '../../components/fashion/MatrizSeleccionDialog';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import ObservacionesConNotaProveedor from '../../components/ObservacionesConNotaProveedor';
import UbicacionSearch from '../../components/UbicacionSearch';
import { useAutoUbicacionPredeterminada } from '../../hooks/useAutoUbicacionPredeterminada';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import productoService from '../../services/productoService';
import {
  cargarProductoParaDocumento,
  clearRechazoDiscontinuado,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  evaluarUsoProductoDocumento,
  mensajeArticuloDiscontinuadoBloqueado,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import ordenCompraService from '../../services/ordenCompraService';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import ItemNotasTextField from '../../components/ItemNotasTextField';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  cantidadSugeridaDesdePendiente,
  costoBrutoDesdePendienteMaster,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  porcDescDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import { formatCurrency } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA',
];

const ITEM_NOTAS_MAX = 500;

interface OcItemForm {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  costoBrutoUnitario: number;
  porcDesc1: number;
  porcDesc2: number;
  porcDesc3: number;
  porcDesc4: number;
  porcDesc5: number;
  um?: string;
  /** Corresponde a items.ite_notas (varchar 500). */
  notas?: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
}

interface OcFormularioForm {
  nroSuc: number;
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  descripcion?: string;
  fechaEmision: string;
  fechaVencim?: string;
  formaDePago: string;
  nroUbicacionDestino?: number;
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
}

const defaultItem: OcItemForm = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  um: '',
  notas: '',
};

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const DecimalTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  sx?: object;
  inputProps?: object;
}> = ({ value, onChange, placeholder, label, fullWidth, sx, inputProps }) => {
  const [localValue, setLocalValue] = useState<string>(String(value ?? 0));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setLocalValue(String(value ?? 0));
  }, [value, focused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = String(e.target.value ?? '');
    raw = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    const parts = raw.split('.');
    if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`;
    setLocalValue(raw);
    if (raw === '' || raw === '.' || raw.endsWith('.')) return;
    const n = Number(raw);
    if (!Number.isNaN(n) && Number.isFinite(n)) onChange(n);
  };

  const handleBlur = () => {
    setFocused(false);
    const currentV = Number(value ?? 0);
    const raw = String(localValue ?? '').trim();
    const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    if (normalized === '' || normalized === '.') {
      setLocalValue('0');
      if (currentV !== 0) onChange(0);
      return;
    }
    const n = Number(normalized);
    const finalV = Number.isNaN(n) ? 0 : n;
    setLocalValue(String(finalV));
    if (finalV !== currentV) onChange(finalV);
  };

  return (
    <TextField
      size="small"
      type="text"
      label={label}
      fullWidth={fullWidth}
      value={localValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      sx={sx}
      inputProps={{
        inputMode: 'decimal',
        ...inputProps,
      }}
    />
  );
};

const OrdenCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const [formulario, setFormulario] = useState<OcFormularioForm>({
    nroSuc: 0,
    nroProveedor: 0,
    nombre: '',
    cuit: '',
    descripcion: '',
    fechaEmision: todayIsoDate(),
    fechaVencim: todayIsoDate(),
    formaDePago: 'CONTADO',
    nroUbicacionDestino: undefined,
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const [proveedorNombre, setProveedorNombre] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);

  const [itemsProductos, setItemsProductos] = useState<OcItemForm[]>([]);
  const [accordionProductosExpanded, setAccordionProductosExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productos, setProductos] = useState<any[]>([]);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);

  const [openPendientes, setOpenPendientes] = useState(false);
  const [matrizDialogOpen, setMatrizDialogOpen] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);

  const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();

  const setNroSucFormulario = useCallback((nroSuc: number) => {
    setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
  }, []);

  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc: Number(formulario.nroSuc) || 0,
    setNroSuc: setNroSucFormulario,
    sucursales,
    loadingSucursales,
  });

  useEffect(() => {
    const v = Number(formulario.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      persistGlobalNroSuc(v);
    }
  }, [formulario.nroSuc]);

  const handleFormularioChange = (updates: Partial<OcFormularioForm>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const updateItemProducto = (index: number, updates: Partial<OcItemForm>) => {
    setItemsProductos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeItemProducto = (index: number) => {
    setItemsProductos((prev) => prev.filter((_, i) => i !== index));
  };

  const addItemProducto = () => {
    setItemsProductos((prev) => [...prev, { ...defaultItem }]);
  };

  const subtotalItem = (item: OcItemForm) => {
    const qty = Number(item.cantidad) || 0;
    const costo = Number(item.costoBrutoUnitario) || 0;
    const d1 = 1 - (Number(item.porcDesc1) || 0) / 100;
    const d2 = 1 - (Number(item.porcDesc2) || 0) / 100;
    const d3 = 1 - (Number(item.porcDesc3) || 0) / 100;
    const d4 = 1 - (Number(item.porcDesc4) || 0) / 100;
    const d5 = 1 - (Number(item.porcDesc5) || 0) / 100;
    return Math.round(qty * costo * d1 * d2 * d3 * d4 * d5 * 100) / 100;
  };

  const subtotalBruto = itemsProductos.reduce((sum, it) => sum + subtotalItem(it), 0);
  const factorBonif =
    (1 - (Number(formulario.porcDesc) || 0) / 100) *
    (1 - (Number(formulario.porcDesc2) || 0) / 100) *
    (1 - (Number(formulario.porcDesc3) || 0) / 100);
  const totalBonificacion = Math.round(subtotalBruto * (1 - factorBonif) * 100) / 100;
  const subtotalNeto = Math.round((subtotalBruto - totalBonificacion) * 100) / 100;

  const sucursalesOrdenadas = useMemo(
    () => [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc),
    [sucursales]
  );

  const handleProveedorSelect = (prov: any) => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: prov.nroProveedor,
      nombre: prov.nombre,
      cuit: prov.cuit,
    }));
    setProveedorNombre(prov.nombre || '');
  };

  const handleProveedorClear = () => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: 0,
      nombre: '',
      cuit: '',
    }));
    setProveedorNombre('');
  };

  const handleUbicacionSelect = (u: any) => {
    setFormulario((f) => ({
      ...f,
      nroUbicacionDestino: u.nroUbicacion,
    }));
    setUbicacionNombre(u.nombreUbicacion || '');
    setOpenUbicacionSearch(false);
  };

  const applyUbicacionPredeterminada = useCallback((nro: number, nombre: string) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: nro }));
    setUbicacionNombre(nombre);
  }, []);

  useAutoUbicacionPredeterminada({
    nroUbicacionDestino: formulario.nroUbicacionDestino,
    onApply: applyUbicacionPredeterminada,
  });

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...paramsBloqueo,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) loadProductos();
  }, [openProductSearch, loadProductos]);

  const handleSelectProduct = async (p: any) => {
    const idx = productSearchIndex;
    if (idx === null) return;
    const codigo = p.codigoArticulo || p.codigo || '';
    clearRechazoDiscontinuado('COMPRAS', codigo);
    if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis)) {
      enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
      return;
    }
    const decision = await evaluarUsoProductoDocumento(
      {
        codigoArticulo: codigo,
        tipoArticulo: p.tipoArticulo,
        descripcion: p.descripcion || p.descripcionArticulo,
      },
      'COMPRAS',
    );
    if (!decision.permitido) {
      if (decision.motivo !== 'discontinuado_rechazado') {
        enqueueSnackbar(decision.mensaje, { variant: 'warning' });
      }
      return;
    }
    const desc = p.descripcion || p.descripcionArticulo || '';
    const costo = Number(p.costo) || Number(p.costoBrutoUnitario) || 0;
    let um = String(p.idUnidadMedida || p.um || '').trim();
    try {
      const umRes = await productoService.getUmCompra(String(codigo), formulario.nroProveedor || null);
      um = umRes.umCompra || um;
    } catch {
      /* fallback a UM del producto */
    }
    updateItemProducto(idx, {
      codigoArticulo: String(codigo),
      descripcion: String(desc),
      costoBrutoUnitario: costo,
      um,
    });
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const openBuscarPendientes = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, true);
      setPendientes(data);
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar pendientes'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const agregarPendienteLinea = async (p: CompraPendienteMaster) => {
    const cantidad = cantidadSugeridaDesdePendiente(p);
    let costoBruto = costoBrutoDesdePendienteMaster(p);
    const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = porcDescDesdePendienteMaster(p);
    if (costoBruto <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
        costoBruto = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
      } catch {
        /* sin maestro ni stock */
      }
    }
    let um = '';
    if ((p.codigoArticulo || '').trim()) {
      try {
        const umRes = await productoService.getUmCompra(
          (p.codigoArticulo || '').trim(),
          formulario.nroProveedor || null
        );
        um = umRes.umCompra || '';
      } catch {
        /* ignore */
      }
    }
    setItemsProductos((prev) => [
      ...prev,
      {
        ...defaultItem,
        codigoArticulo: p.codigoArticulo || '',
        descripcion: p.descripcionArticulo || '',
        cantidad,
        costoBrutoUnitario: costoBruto,
        porcDesc1,
        porcDesc2,
        porcDesc3,
        porcDesc4,
        porcDesc5,
        um,
        nroFormularioMaster: p.nroFormularioMaster,
        nroItemMaster: p.nroItemMaster,
      },
    ]);
  };

  const handleCodigoBlurOrEnter = useCallback(
    async (idx: number, codigoValue: string) => {
      const codigo = (codigoValue || '').trim();
      if (!codigo) return;
      try {
        const modo = (await paramsBusquedaProductoDocumento('COMPRAS')).modoBloqueoArtDiscontinuados ?? 1;
        setModoBloqueoDis(modo);
        const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
        if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modo)) {
          enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
          return;
        }
        const decision = await evaluarUsoProductoDocumento(
          {
            codigoArticulo: codigo,
            tipoArticulo: p.tipoArticulo,
            descripcion: (p as any).descripcion || (p as any).descripcionArticulo,
          },
          'COMPRAS',
        );
        if (!decision.permitido) {
          if (decision.motivo === 'discontinuado_rechazado') {
            setItemsProductos((prev) => {
              const next = [...prev];
              if (!next[idx]) return prev;
              next[idx] = { ...next[idx], codigoArticulo: '', descripcion: '', costoBrutoUnitario: 0, um: '' };
              return next;
            });
          } else {
            enqueueSnackbar(decision.mensaje, { variant: 'warning' });
          }
          return;
        }
        const desc = (p as any).descripcion || (p as any).descripcionArticulo || '';
        const costo = Number((p as any).costo) || Number((p as any).costoBrutoUnitario) || 0;
        let um = String((p as any).idUnidadMedida || (p as any).um || '').trim();
        try {
          const umRes = await productoService.getUmCompra(codigo, formulario.nroProveedor || null);
          um = umRes.umCompra || um;
        } catch {
          /* ignore */
        }
        setItemsProductos((prev) => {
          const next = [...prev];
          if (!next[idx]) return prev;
          next[idx] = { ...next[idx], codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo, um };
          return next;
        });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar, formulario.nroProveedor]
  );

  const handleSubmit = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe seleccionar una sucursal de venta', { variant: 'warning' });
      return;
    }
    if (!loadingSucursales && sucursalesOrdenadas.length === 0) {
      enqueueSnackbar('No tiene sucursales asignadas. Solicite asignación a un administrador.', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino válida', { variant: 'warning' });
      return;
    }
    if (!formulario.formaDePago?.trim()) {
      enqueueSnackbar('Debe seleccionar una forma de pago', { variant: 'warning' });
      return;
    }

    const itemsValidos = itemsProductos.filter(
      (it) =>
        (it.codigoArticulo || '').trim().length > 0 &&
        Number(it.cantidad) > 0 &&
        Number(it.costoBrutoUnitario) > 0
    );

    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem válido (código, cantidad y costo unitario > 0)', {
        variant: 'warning',
      });
      return;
    }

    const payload = {
      formulario: {
        nroSuc: Number(formulario.nroSuc),
        nroProveedor: Number(formulario.nroProveedor),
        nombre: formulario.nombre || undefined,
        cuit: formulario.cuit || undefined,
        descripcion: formulario.descripcion?.trim() || undefined,
        fechaEmision: formulario.fechaEmision || todayIsoDate(),
        fechaVencim: formulario.fechaVencim || undefined,
        formaDePago: formulario.formaDePago,
        nroUbicacionDestino: formulario.nroUbicacionDestino,
        porcDesc: Number(formulario.porcDesc) || 0,
        porcDesc2: Number(formulario.porcDesc2) || 0,
        porcDesc3: Number(formulario.porcDesc3) || 0,
      },
      items: itemsValidos.map((it) => ({
        codigoArticulo: it.codigoArticulo.trim(),
        descripcion: it.descripcion?.trim() || undefined,
        cantidad: Number(it.cantidad),
        costoBrutoUnitario: Number(it.costoBrutoUnitario),
        porcDesc1: Number(it.porcDesc1) || 0,
        porcDesc2: Number(it.porcDesc2) || 0,
        porcDesc3: Number(it.porcDesc3) || 0,
        porcDesc4: Number(it.porcDesc4) || 0,
        porcDesc5: Number(it.porcDesc5) || 0,
        um: (it.um || '').trim() || undefined,
        ...(it.nroFormularioMaster != null &&
        it.nroItemMaster != null &&
        it.nroFormularioMaster > 0 &&
        it.nroItemMaster > 0
          ? { nroFormularioMaster: it.nroFormularioMaster, nroItemMaster: it.nroItemMaster }
          : {}),
        ...(() => {
          const n = (it.notas || '').trim();
          return n ? { notas: n.slice(0, ITEM_NOTAS_MAX) } : {};
        })(),
      })),
    };

    try {
      setSaving(true);
      const created = await ordenCompraService.createOrdenCompra(payload);
      enqueueSnackbar('Orden de compra creada correctamente', { variant: 'success' });
      navigate(`/compras/ordenes/${created.nroFormulario}`);
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear la orden de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const handleSucursalChange = (v: number | null) => {
    onSucursalChange(v != null && v > 0 ? v : 0);
  };

  const fechaEmisionStr = formulario.fechaEmision || todayIsoDate();
  const fechaVencimStr = formulario.fechaVencim || '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/ordenes')}
              sx={{ textTransform: 'none' }}
            >
              Órdenes de Compra
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
            >
              Nueva Orden de Compra
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0 && proveedorNombre
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre,
                        cuit: formulario.cuit || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <SearchableSelect<number>
                label="Nº Sucursal"
                required
                disabled={loadingSucursales || sucursalesOrdenadas.length === 0}
                loading={loadingSucursales}
                allowEmpty
                emptyOptionLabel={
                  loadingSucursales
                    ? 'Cargando…'
                    : sucursalesOrdenadas.length === 0
                      ? 'Sin sucursales'
                      : 'Seleccione'
                }
                value={formulario.nroSuc > 0 ? formulario.nroSuc : null}
                onChange={handleSucursalChange}
                options={sucursalesOrdenadas.map((s) => ({
                  value: s.nroSuc,
                  label: `${s.nroSuc} - ${s.nombreSucursal?.trim() || `Sucursal ${s.nroSuc}`}`,
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={fechaEmisionStr}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Fecha probable de entrega"
                type="date"
                value={fechaVencimStr}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                label="Forma de pago"
                required
                value={formulario.formaDePago || 'CONTADO'}
                onChange={(v) => handleFormularioChange({ formaDePago: v ?? 'CONTADO' })}
                options={FORMAS_DE_PAGO.map((fp) => ({ value: fp, label: fp }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Ubicación destino"
                value={ubicacionNombre || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onClick={() => setOpenUbicacionSearch(true)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <ObservacionesConNotaProveedor nroProveedor={formulario.nroProveedor}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={formulario.descripcion || ''}
                  onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
                />
              </ObservacionesConNotaProveedor>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Detalle
          </Typography>
          {!formulario.nroProveedor || formulario.nroProveedor <= 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione un proveedor para poder cargar ítems.
            </Typography>
          ) : null}
          <Box
            sx={{
              mb: 2,
              opacity: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 0.5 : 1,
              pointerEvents: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 'none' : 'auto',
            }}
          >
            <Accordion expanded={accordionProductosExpanded} onChange={(_, exp) => setAccordionProductosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Compras</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                {isMobile ? (
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {itemsProductos.length === 0 ? (
                      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                        Sin ítems. Use &quot;Agregar producto&quot; para añadir líneas.
                      </Typography>
                    ) : (
                      itemsProductos.map((item, idx) => (
                        <Card key={`p-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack spacing={1.25}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Ítem {idx + 1}
                                </Typography>
                                <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error" aria-label="Eliminar ítem">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                              <TextField
                                size="small"
                                fullWidth
                                label="Código"
                                value={item.codigoArticulo || ''}
                                onChange={(e) => {
                                  clearRechazoDiscontinuado('COMPRAS', item.codigoArticulo);
                                  clearRechazoDiscontinuado('COMPRAS', e.target.value);
                                  updateItemProducto(idx, { codigoArticulo: e.target.value });
                                }}
                                onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setProductSearchIndex(idx);
                                          setOpenProductSearch(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label="Descripción"
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                              />
                              <ItemNotasTextField
                                fullWidth
                                label="Notas"
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                onChange={(notas) => updateItemProducto(idx, { notas })}
                              />
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Cant."
                                    value={item.cantidad ?? 1}
                                    onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="UM"
                                    value={item.um || ''}
                                    onChange={(e) => updateItemProducto(idx, { um: e.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Costo unit."
                                    value={item.costoBrutoUnitario ?? 0}
                                    onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                                    inputProps={{ min: 0, step: 1 }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="% Desc 1"
                                    value={item.porcDesc1 ?? 0}
                                    onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 0.5 }}>
                                    <Typography variant="caption" color="primary.dark" fontWeight={600} display="block">
                                      Subtotal
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary.dark">
                                      {formatCurrency(subtotalItem(item))}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                ) : (
                  <TableContainer sx={{ mb: 2, maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            Cant.
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            Costo unit.
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            % Desc 1
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            Subtotal
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemsProductos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                              Sin ítems. Use &quot;Agregar producto&quot; para añadir líneas.
                            </TableCell>
                          </TableRow>
                        ) : (
                          itemsProductos.map((item, idx) => (
                            <TableRow key={`p-${idx}`}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={item.codigoArticulo || ''}
                                  onChange={(e) => {
                                    clearRechazoDiscontinuado('COMPRAS', item.codigoArticulo);
                                    clearRechazoDiscontinuado('COMPRAS', e.target.value);
                                    updateItemProducto(idx, { codigoArticulo: e.target.value });
                                  }}
                                  onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                    }
                                  }}
                                  placeholder="Código"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setProductSearchIndex(idx);
                                            setOpenProductSearch(true);
                                          }}
                                        >
                                          <SearchIcon fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{ minWidth: 120 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={item.descripcion || ''}
                                  onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                                  placeholder="Descripción"
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 180 }}>
                                <ItemNotasTextField
                                  fullWidth
                                  value={item.notas ?? ''}
                                  maxLength={ITEM_NOTAS_MAX}
                                  placeholder="Notas"
                                  onChange={(notas) => updateItemProducto(idx, { notas })}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ width: 90 }}>
                                <DecimalTextField
                                  value={item.cantidad ?? 1}
                                  onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                                  inputProps={{ min: 0.01, step: 0.01 }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 80 }}>
                                <TextField
                                  size="small"
                                  value={item.um || ''}
                                  onChange={(e) => updateItemProducto(idx, { um: e.target.value })}
                                  sx={{ width: 70 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ width: 110 }}>
                                <DecimalTextField
                                  value={item.costoBrutoUnitario ?? 0}
                                  onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 120 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ width: 80 }}>
                                <DecimalTextField
                                  value={item.porcDesc1 ?? 0}
                                  onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                                  sx={{ width: 70 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(subtotalItem(item))}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ '& > *': { width: { xs: '100%', sm: 'auto' } } }}
                >
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addItemProducto}
                    variant="outlined"
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Agregar producto
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setMatrizDialogOpen(true)}
                    sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                  >
                    Agregar por matriz
                  </Button>
                  <Button variant="outlined" onClick={openBuscarPendientes} sx={{ borderColor: '#1976d2', color: '#1976d2' }}>
                    Buscar pendientes
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Totales
          </Typography>
          <Card sx={{ mt: 2, boxShadow: 2, borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Subtotal Bruto:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {formatCurrency(subtotalBruto)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.300', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: 'text.secondary' }}>
                  Bonificación Aplicada
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <DecimalTextField
                    value={formulario.porcDesc ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc: v })}
                    sx={{ width: { xs: 72, sm: 80 }, flex: { xs: '1 1 72px', sm: '0 0 auto' } }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                  <DecimalTextField
                    value={formulario.porcDesc2 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc2: v })}
                    sx={{ width: { xs: 72, sm: 80 }, flex: { xs: '1 1 72px', sm: '0 0 auto' } }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                  <DecimalTextField
                    value={formulario.porcDesc3 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc3: v })}
                    sx={{ width: { xs: 72, sm: 80 }, flex: { xs: '1 1 72px', sm: '0 0 auto' } }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Total Bonificación Aplicada:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                    {formatCurrency(totalBonificacion)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Subtotal Neto:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {formatCurrency(subtotalNeto)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="flex-end"
            spacing={2}
            sx={{ '& > *': { width: { xs: '100%', sm: 'auto' } } }}
          >
            <Button variant="outlined" onClick={() => navigate('/compras/ordenes')} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              fullWidth={isMobile}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </Stack>
          <NumeroInternoMeta nro={0} />
        </CardContent>
      </Card>

      <UbicacionSearch open={openUbicacionSearch} onClose={() => setOpenUbicacionSearch(false)} onSelect={handleUbicacionSelect} />

      <Dialog
        open={openPendientes}
        onClose={() => setOpenPendientes(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Buscar pendientes
          </Typography>
          <IconButton aria-label="Cerrar" onClick={() => setOpenPendientes(false)} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
          {loadingPendientes ? (
            <CircularProgress size={22} sx={{ display: 'block', my: 2 }} />
          ) : pendientes.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No hay líneas pendientes para este proveedor.
            </Typography>
          ) : isMobile ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {pendientes.map((p) => (
                <Card key={`${p.nroFormularioMaster}-${p.nroItemMaster}`} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {formatComprobanteMasterDisplay(p)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {p.nroFormularioMaster}/{p.nroItemMaster}
                        </Typography>
                      </Stack>
                      <Box
                        sx={(t) => ({
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: t.palette.primary.light + '14',
                        })}
                      >
                        <Typography variant="caption" color="text.secondary" display="block">
                          Artículo
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                          {p.codigoArticulo} - {p.descripcionArticulo}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Emisión: {formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pedida
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {p.cantidadPedida}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pend. facturar
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {p.pendienteFacturar}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pend. recibir
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {p.pendienteRecibir}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPendienteHiloInfo({
                              nroFormularioMaster: p.nroFormularioMaster,
                              nroItemMaster: p.nroItemMaster,
                              resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                            })
                          }
                          aria-label="Información facturado y remitido"
                        >
                          <InfoOutlinedIcon fontSize="small" color="info" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          onClick={() => void agregarPendienteLinea(p)}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                        >
                          Agregar
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ mt: 1, maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Master</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. pedida</TableCell>
                    <TableCell align="right">Pend. facturar</TableCell>
                    <TableCell align="right">Pend. recibir</TableCell>
                    <TableCell align="center" sx={{ width: 44 }} />
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientes.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell>
                        {p.nroFormularioMaster}/{p.nroItemMaster}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}
                      </TableCell>
                      <TableCell>
                        {p.codigoArticulo} - {p.descripcionArticulo}
                      </TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{p.pendienteFacturar}</TableCell>
                      <TableCell align="right">{p.pendienteRecibir}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver documentos facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => void agregarPendienteLinea(p)}>
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setOpenPendientes(false)} fullWidth={isMobile} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Buscar artículo
          </Typography>
          <IconButton
            aria-label="Cerrar"
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  loadProductos();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2', flexShrink: 0 }}
            >
              Buscar
            </Button>
          </Stack>

          {isMobile ? (
            <Stack spacing={1.5}>
              {productos.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                  Sin resultados.
                </Typography>
              ) : (
                productos.map((p) => {
                  const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                  const esDis = esTipoDiscontinuado(p.tipoArticulo);
                  return (
                  <Card key={p.codigoArticulo || p.codigo || ''} variant="outlined" sx={{ borderRadius: 2, opacity: disBloqueado ? 0.75 : 1 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                          {p.codigoArticulo || p.codigo || '-'}
                        </Typography>
                        {esDis && <Chip size="small" label="DIS" color="warning" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                        {p.descripcion || p.descripcionArticulo || '-'}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={disBloqueado}
                        onClick={() => handleSelectProduct(p)}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                      </Button>
                    </CardContent>
                  </Card>
                  );
                })
              )}
            </Stack>
          ) : (
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center">Seleccionar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productos.map((p) => {
                    const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                    const esDis = esTipoDiscontinuado(p.tipoArticulo);
                    return (
                    <TableRow key={p.codigoArticulo || p.codigo || ''} hover sx={{ opacity: disBloqueado ? 0.75 : 1 }}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{p.codigoArticulo || p.codigo || '-'}</span>
                          {esDis && <Chip size="small" label="DIS" color="warning" />}
                        </Stack>
                      </TableCell>
                      <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                      <TableCell align="center">
                        <Button size="small" disabled={disBloqueado} onClick={() => handleSelectProduct(p)}>
                          {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            fullWidth={isMobile}
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <MatrizSeleccionDialog
        open={matrizDialogOpen}
        onClose={() => setMatrizDialogOpen(false)}
        onConfirm={(lineas) => {
          setItemsProductos((prev) => [
            ...prev,
            ...lineas.map((l) => ({
              ...defaultItem,
              codigoArticulo: l.codigoArticulo,
              descripcion: l.descripcion,
              cantidad: l.cantidad,
              costoBrutoUnitario: l.costoUnitario ?? l.precioUnitario ?? 0,
            })),
          ]);
          enqueueSnackbar(`${lineas.length} línea(s) agregadas desde matriz`, { variant: 'success' });
        }}
      />

    </Box>
  );
};

export default OrdenCompraForm;